import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, AuthContextType } from '../types';
import { authService } from '../services/authService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const storedUser = authService.getStoredUser();
        if (storedUser && authService.isAuthenticated()) {
            setUser(storedUser);
        }
        setIsLoading(false);
    }, []);

    const login = useCallback(async (email: string, password: string, captchaToken?: string): Promise<void> => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await authService.adminLogin(email, password, captchaToken);

            if (response.success && response.data) {
                setUser(response.data.user);
            } else {
                throw new Error(response.message || 'Login failed');
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred during login');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loginWithToken = useCallback(async (code: string): Promise<void> => {
        setIsLoading(true);
        setError(null);

        try {
            console.log('[AuthContext] loginWithToken called with token');
            const response = await authService.loginWithToken(code);

            if (response.success && response.data) {
                console.log('[AuthContext] Token login successful, setting user');
                setUser(response.data.user);
            } else {
                throw new Error(response.message || 'Token login failed');
            }
        } catch (err: any) {
            console.error('[AuthContext] Token login error:', err);
            setError(err.message || 'An error occurred during authentication');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        try {
            await authService.logout();
            setUser(null);
            setError(null);

            localStorage.clear();

        } catch (err: any) {
            console.error('Logout error:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            isLoading,
            login,
            loginWithToken,
            logout,
            error
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};